import IconComponent from "@mdi/react";
import { useEffect, useRef, useState } from "react";
import { InPortal, OutPortal } from "react-reverse-portal";

import { mdiAlertCircleOutline } from "@mdi/js";
import {
  selectWellCardCustomLineActive,
  selectWellCardDrawArmed,
  selectWellCardGunbarrelView,
  selectWellCardTargetUwi,
  useWellCardPanelStore
} from "stores/wellCardPanel";
import styled from "styled-components";

import { WellDetailCardData } from "api/data";

import { ToolbarButton } from "components/base";
import { useDashboardContext } from "components/dashboard/hooks/useDashboardContext";
import Line from "components/icons/Line";
import { useScreenshotContext } from "components/screenshot/hooks";

import { PanelErrorState, ViewerSpinner } from "../PanelStates";
import { SpacingMetricsRow } from "../blocks/SpacingMetricsRow";
import {
  WELL_CARD_PANEL_THREE_D_WIDGET_ID,
  WELL_CARD_PANEL_XDA_WIDGET_ID
} from "../constants";
import { GunbarrelViewSwitcher } from "../gunbarrel/GunbarrelViewSwitcher";
import { GunbarrelThreeDViewer, GunbarrelXdaViewer } from "../gunbarrel/GunbarrelViewers";
import { HalfWidthControl } from "../gunbarrel/HalfWidthControl";
import { DeriveXdaLineResult } from "../hooks/useDeriveXdaLineForWell";
import { useEmbedScreenshotSheet } from "../hooks/useEmbedScreenshotSheet";
import { useLineHalfLengthMeters } from "../hooks/useLineHalfLengthMeters";

export interface GunbarrelTabProps {
  wellData: Maybe<WellDetailCardData>;
  line: DeriveXdaLineResult;
}

/**
 * A definite-height flex column, never a scroll column: both viewers measure
 * their container, and only the active one is mounted — a hidden viewer
 * measures zero and never recovers.
 */
export function GunbarrelTab({ wellData, line }: GunbarrelTabProps): JSX.Element {
  const { hasLine, isDeriving, isError, neighboursUnavailable, retry } = line;
  const view = useWellCardPanelStore(selectWellCardGunbarrelView);
  const setGunbarrelView = useWellCardPanelStore((s) => s.setGunbarrelView);
  const halfLengthMeters = useLineHalfLengthMeters();
  const setLineHalfLengthMeters = useWellCardPanelStore((s) => s.setLineHalfLengthMeters);
  const customLineActive = useWellCardPanelStore(selectWellCardCustomLineActive);
  const drawArmed = useWellCardPanelStore(selectWellCardDrawArmed);
  // The card is about one well, so that well is the reference the viewer takes
  // its spacing measurements from — no in-plot click needed.
  const targetUwi = useWellCardPanelStore(selectWellCardTargetUwi);
  const requestLineDraw = useWellCardPanelStore((s) => s.requestLineDraw);
  const cancelLineDraw = useWellCardPanelStore((s) => s.cancelLineDraw);

  // Leaving this tab gives up a draw that is still waiting for its two clicks.
  // The button that explains the armed map goes with the tab, so what is left
  // is a crosshair and a map that swallows the next click for no visible
  // reason. A line already drawn is untouched: it is the user's until the card
  // is pointed at another well.
  const drawArmedRef = useRef(drawArmed);
  drawArmedRef.current = drawArmed;
  useEffect(
    () => () => {
      if (drawArmedRef.current) cancelLineDraw();
    },
    [cancelLineDraw]
  );

  // Whichever viewer is mounted renders its own toolbar into this slot, so the
  // tab shows one bar rather than a bar plus a strip that hovers over the plot.
  const [toolbarSlot, setToolbarSlot] = useState<Maybe<HTMLDivElement>>(null);

  const { capabilities } = useDashboardContext();
  // Drawing a line is a map gesture, so it needs a map to draw on.
  const canDrawLine = !!capabilities?.hasMapWidget;

  const isXda = view === "xda";

  /*
   * Is the viewer on this tab the one being screenshotted? The same test the
   * viewer runs on itself, read from the same context, and against the card's
   * own name rather than the viewer's kind - so a dashboard widget of the same
   * kind entering screenshot mode is not this one.
   */
  const { widget: screenshotWidget } = useScreenshotContext();
  const screenshotWidgetId = isXda
    ? WELL_CARD_PANEL_XDA_WIDGET_ID
    : WELL_CARD_PANEL_THREE_D_WIDGET_ID;
  const screenshotActive = screenshotWidget?.widgetId === screenshotWidgetId;
  const { node: viewerNode, inSheet } = useEmbedScreenshotSheet(
    isXda ? "xda" : "3d",
    screenshotActive
  );
  // `hasLine` is "the map store holds a line", so a user-drawn line counts the
  // same as a derived one. Nothing here needs to know which flow wrote it.
  //
  // A settled answer of "no line for this well" mounts the viewer too: it draws
  // an empty scene under its own "no lines drawn" warning, which is what the
  // standalone widget shows, and leaves the user a section to draw into.
  const showSpinner = isXda && !hasLine && isDeriving;
  const showLineError = isXda && !hasLine && !isDeriving && isError;
  const showViewer = !isXda || hasLine || (!isDeriving && !isError);

  return (
    <Wrapper data-testid="well-card-panel-gunbarrel">
      {/* Dimmed only over the error state, and both rows together: they read
          as one band, so dimming the card's own controls and leaving the
          viewer's strip beside them bright says half the band is still live.
          The controls stay live with no line, because the viewer under them is
          live and the draw button is the way out of that state. */}
      <Toolbar $dim={showLineError}>
        <GunbarrelViewSwitcher view={isXda ? "xda" : "3d"} onChange={setGunbarrelView} />
        {/* Shown in both views: the line these controls govern is the one the
            3D scene draws its section from as well, so it is the same request
            either way. */}
        <HalfWidthControl
          halfLengthMeters={halfLengthMeters}
          onChange={setLineHalfLengthMeters}
          disabled={customLineActive}
        />
        {/* The map's own cross-section tool, not a lookalike: same component,
            same icon and the same active treatment, so the two ways into a
            section read as one control - and that includes standing itself
            down once the two clicks are in. Lit means a draw is waiting for
            them, nothing more: pressed while lit it gives the draw up, and
            pressed again afterwards it starts another. A drawn line is not
            undone from here. It belongs to the user until the card is pointed
            at another well, which is what the dashboard's own section does
            too, and the Length control says so while it is dead. */}
        <ToolbarButton
          active={drawArmed}
          disabled={!canDrawLine}
          icon={<Line />}
          onToggle={() => {
            if (drawArmed) {
              cancelLineDraw();
              return;
            }
            requestLineDraw();
          }}
          /* Not "cancel": nothing is undone, the map just stops waiting for
             the two clicks. */
          tooltipText={drawArmed ? "Stop drawing" : "Draw cross-section line"}
          btnDisabledReason="No map on this dashboard"
          overflowLabel=""
          styleKey="mapToolbar"
          data-testid="well-card-draw-line"
        />
      </Toolbar>

      <Toolbar $dim={showLineError}>
        <ViewerToolbarSlot
          ref={setToolbarSlot}
          data-testid="well-card-viewer-toolbar-slot"
        />
      </Toolbar>

      {neighboursUnavailable && showViewer && (
        <NeighbourNotice data-testid="well-card-neighbours-unavailable">
          <IconComponent path={mdiAlertCircleOutline} size={0.58} />
          The wells this section crosses could not be fetched, so it shows this well
          alone.
        </NeighbourNotice>
      )}

      {showSpinner ? (
        <ViewerArea>
          <ViewerSpinner />
        </ViewerArea>
      ) : showLineError ? (
        <ViewerArea>
          <PanelErrorState
            title="Could not derive the section line"
            detail="The request for this well's cross-section line failed. This is not the same as a well with no directional survey."
            onRetry={retry}
            testId="well-card-panel-line-error"
          />
        </ViewerArea>
      ) : (
        <ViewerArea>
          {/* The viewer keeps its place in this tree wherever its DOM is shown,
              so the scene, the camera and the settings store all survive the
              move into the card's fullscreen sheet and back.

              Its toolbar undocks for that state. In the card the toolbar is
              portalled up into the band above, which is inside the card and so
              hidden while the sheet is up - the camera that got the user here
              would go with it. With no container the viewer draws its own
              toolbar over the plot, which is where a dashboard widget's lives
              in fullscreen. */}
          {/* Each branch reads what its viewer last saved as that viewer
              mounts, which these wrappers exist for: the switcher swaps the
              branch without unmounting this tab, so a read taken here would be
              a snapshot from the tab's own mount and stale from the first
              switch on. See GunbarrelViewers for the whole of it. */}
          <InPortal node={viewerNode}>
            {isXda ? (
              <GunbarrelXdaViewer
                toolbarContainer={inSheet ? null : toolbarSlot}
                screenshotWidgetId={screenshotWidgetId}
                referenceUwi={targetUwi}
              />
            ) : (
              <GunbarrelThreeDViewer
                toolbarContainer={inSheet ? null : toolbarSlot}
                screenshotWidgetId={screenshotWidgetId}
              />
            )}
          </InPortal>
          {!inSheet && <OutPortal node={viewerNode} />}
        </ViewerArea>
      )}

      <SpacingMetricsRow wellData={wellData} />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// No vertical rules between the groups: the controls are spaced apart, and the
// only separator left in the band is the one inside the viewer's own icon
// strip. Bars everywhere else made a short row read as four crowded panes.
//
// Two of these stack: the card's own controls, then the viewer's docked strip.
// Neither shares its width with the other, because the strip does not all fit
// beside them — fifteen 30px buttons with their gaps and a rule come to 571px,
// and the switcher, length box and draw button want another 330 of the 778
// there are. Each row is a fixed height, nothing wraps and nothing is clipped;
// what still does not fit in the strip's own row collapses behind its "..."
// menu, the way the chart toolbar does.
const Toolbar = styled.div<{ $dim?: boolean }>`
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 10px;
  border-bottom: 1px solid var(--fog);
  opacity: ${(props) => (props.$dim ? 0.45 : 1)};
`;

// Takes its whole row's width, rather than sizing to its own content: that
// width IS what the docked strip measures itself against to decide how many
// buttons fit. Right-aligned inside it, so the strip still reads as the
// right-hand end of the bar, and clipped rather than allowed to paint outside
// the panel on the frame before the first measurement lands.
const ViewerToolbarSlot = styled.div`
  flex: 1;
  min-width: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  overflow: hidden;
`;

const NeighbourNotice = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fff8e6;
  border-bottom: 1px solid #f2e2b8;
  color: #7a5c12;
  font-size: 12px;
`;

const ViewerArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;
